import { Injectable } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable, map, tap, catchError, of } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { FirebaseTokenData } from '../../shared/types/user.types';
import { TranslateService } from '../services/translate.service';
import { AppRoutes, ROUTE_PATHS } from '../../shared/constants/app-routes';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {
  constructor(
    private authService: AuthService,
    private router: Router,
    private translateService: TranslateService
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    // Si está autenticado pero no aceptó términos, redirigir a términos
    if (this.authService.isAuthenticated()) {
      const user = this.authService.currentUser();
      if (user?.termsAccepted !== true) {
        this.router.navigate([ROUTE_PATHS[AppRoutes.TERMS_AND_CONDITIONS]]);
        return of(false);
      }
    }

    const token = route.queryParams?.['token'];

    // Si ya está autenticado y el token es el mismo, verificar si tiene información completa de sesión
    if (this.authService.isAuthenticated() && token && !this.authService.isNewToken(token)) {
      return this.ensureCompleteSession().pipe(
        map(() => true),
        catchError(() => of(true)) // Si falla, permitir acceso de todos modos
      );
    }

    // Si no está autenticado O el token es nuevo, procesar token de URL completo
    return this.processFirebaseTokenFromUrl(route).pipe(
      map(() => {
        if (this.authService.isAuthenticated()) {
          return true;
        }
        return false;
      }),
      catchError(() => {
        this.router.navigate(['/unauthorized']);
        return of(false);
      })
    );
  }

  // Procesa el token de Firebase desde la URL y obtiene sesión completa
  private processFirebaseTokenFromUrl(route: ActivatedRouteSnapshot): Observable<void> {
    const token = route.queryParams?.['token'];

    if (token && this.isValidTokenFormat(token)) {
      const firebaseTokenData: FirebaseTokenData = { token };

      // Usar el método completo que obtiene buildingId y unitId
      return this.authService.authenticateWithFirebaseTokenAndSession(firebaseTokenData).pipe(
        tap(() => {
          this.applyUserLanguage();
        }),
        map(() => void 0),
        catchError(error => {
          console.warn('❌ Error obteniendo sesión completa en AuthGuard:', error);
          // Fallback a autenticación básica
          this.authService.authenticateWithFirebaseToken(firebaseTokenData);
          this.applyUserLanguage();
          return of(void 0);
        })
      );
    }

    return of(void 0); // Si no hay token, devolver observable vacío
  }

  // Asegura que el usuario tenga información completa de sesión (buildingId, unitId)
  private ensureCompleteSession(): Observable<void> {
    const { buildingId, unitId } = this.authService.getCurrentUserBuildingAndUnit();

    // Si ya tiene buildingId y unitId, no hacer nada
    if (buildingId && unitId) {
      this.applyUserLanguage();
      return of(void 0);
    }

    // Si no tiene la información completa, intentar actualizarla
    return this.authService.updateUserSessionInfo().pipe(
      tap(response => {
        if (response) {
          this.applyUserLanguage();
        } else {
          console.info('⚠️ No se pudo completar información de sesión para usuario existente');
        }
      }),
      map(() => void 0),
      catchError(error => {
        console.warn('❌ Error completando sesión para usuario existente:', error);
        return of(void 0); // No fallar, permitir acceso
      })
    );
  }

  // Valida el formato básico del token
  private isValidTokenFormat(token: string): boolean {
    return !!(token && typeof token === 'string' && token.length > 10 && token.includes('.'));
  }

  private applyUserLanguage(): void {
    const userLanguage = this.authService.getCurrentUserLanguage();
    this.translateService.setLanguage(userLanguage);
  }
}

